import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Aviso, EstadoSocio, FotoSocio } from "./piezas";
import EscanerQR from "./EscanerQR";
import { useIdioma } from "../lib/idioma";

export interface SocioResumen {
  id: string;
  numeroSocio: number;
  nombre: string;
  apellidos: string;
  documentoNumero: string;
  estado: string;
  fotoUrl: string | null;
  saldoCentimos: number;
}

/**
 * Buscador con sugerencias por nombre, número de socio o documento.
 * Pensado para teclear rápido en el mostrador: flechas para moverse y Enter para elegir.
 */
export default function BuscadorSocios({
  alElegir,
  autoFoco,
  marcador,
  buscarLocal,
}: {
  alElegir: (socio: SocioResumen) => void;
  autoFoco?: boolean;
  marcador?: string;
  /**
   * Cómo buscar cuando no hay red: en el paquete guardado en la tablet. Se
   * usa si el servidor no contesta, para que el mostrador siga encontrando a
   * la gente con la WiFi caída.
   */
  buscarLocal?: (texto: string) => SocioResumen[];
}) {
  const { t } = useIdioma();
  const [texto, setTexto] = useState("");
  const [resultados, setResultados] = useState<SocioResumen[]>([]);
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const [escaneando, setEscaneando] = useState(false);
  const [avisoEscaner, setAvisoEscaner] = useState("");
  const contenedor = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (texto.trim().length < 2) {
      setResultados([]);
      return;
    }
    const temporizador = setTimeout(async () => {
      try {
        const datos = await api<{ socios: SocioResumen[] }>("/socios", {
          query: { buscar: texto.trim(), porPagina: 8 },
        });
        setResultados(datos.socios);
        setAbierto(true);
        setResaltado(0);
      } catch {
        // Sin servidor, lo que haya en la tablet. Sin eso, nada.
        const locales = buscarLocal?.(texto) ?? [];
        setResultados(locales);
        setAbierto(locales.length > 0);
        setResaltado(0);
      }
    }, 220);
    return () => clearTimeout(temporizador);
  }, [texto]);

  useEffect(() => {
    const fuera = (e: MouseEvent) => {
      if (contenedor.current && !contenedor.current.contains(e.target as Node))
        setAbierto(false);
    };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, []);

  function elegir(socio: SocioResumen) {
    alElegir(socio);
    setTexto("");
    setResultados([]);
    setAbierto(false);
  }

  /** Un carnet leido debe identificar a una sola persona; si no, no se elige nadie. */
  async function buscarPorCodigo(codigo: string) {
    setEscaneando(false);
    setAvisoEscaner("");
    try {
      const datos = await api<{ socios: SocioResumen[] }>("/socios", {
        query: { buscar: codigo, porPagina: 2 },
      });
      if (datos.socios.length === 1) elegir(datos.socios[0]!);
      else setAvisoEscaner(t("b.codigoDesconocido"));
    } catch {
      const locales = buscarLocal?.(codigo) ?? [];
      if (locales.length === 1) elegir(locales[0]!);
      else setAvisoEscaner(t("b.falloCodigo"));
    }
  }

  return (
    <div className="buscador" ref={contenedor}>
      <Aviso tipo="atencion">{avisoEscaner}</Aviso>
      <div className="fila" style={{ gap: 8 }}>
        <input
          style={{ flex: 1 }}
          value={texto}
          autoFocus={autoFoco}
          placeholder={marcador ?? t("b.marcador")}
          onChange={(e) => setTexto(e.target.value)}
          onFocus={() => resultados.length > 0 && setAbierto(true)}
          onKeyDown={(e) => {
            if (!abierto || resultados.length === 0) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setResaltado((r) => (r + 1) % resultados.length);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setResaltado(
                (r) => (r - 1 + resultados.length) % resultados.length,
              );
            } else if (e.key === "Enter") {
              e.preventDefault();
              const elegido = resultados[resaltado];
              if (elegido) elegir(elegido);
            } else if (e.key === "Escape") {
              setAbierto(false);
            }
          }}
        />
        <button
          type="button"
          className="boton secundario"
          title={t("b.escanear")}
          onClick={() => {
            setAvisoEscaner("");
            setEscaneando(true);
          }}
        >
          {t("b.escanearBoton")}
        </button>
      </div>

      {abierto && resultados.length > 0 && (
        <div className="resultados">
          {resultados.map((socio, indice) => (
            <button
              key={socio.id}
              type="button"
              className={indice === resaltado ? "resaltado" : ""}
              onMouseEnter={() => setResaltado(indice)}
              onClick={() => elegir(socio)}
            >
              <FotoSocio socio={socio} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong>
                  #{socio.numeroSocio} {socio.nombre} {socio.apellidos}
                </strong>
                <br />
                <span className="suave pequeno">{socio.documentoNumero}</span>
              </span>
              <EstadoSocio estado={socio.estado} />
            </button>
          ))}
        </div>
      )}

      {abierto && texto.trim().length >= 2 && resultados.length === 0 && (
        <div className="resultados">
          <div className="vacio pequeno">
            {t("b.sinCoincidencias", { texto })}
          </div>
        </div>
      )}

      {escaneando && (
        <EscanerQR
          alLeer={buscarPorCodigo}
          alCerrar={() => setEscaneando(false)}
        />
      )}
    </div>
  );
}
